/* eslint-disable react/prop-types */
import { useState } from 'react';
import { Button, Typography, Box } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';

import { t } from '../i18n/translations';
import { NO_COACH_ID } from '../constants';
import { isTrulyConsecutive } from '../utils/slots';
import {
    Subcontainer, Calendar,
    Input, InputContainer, Contact, Subtitle, Found,
} from './Picker.styles';
import SelectForm from './SelectForm';

/**
 * Left column of the booking widget:
 *   calendar → dropdowns → slot multiselect → contact details → action buttons
 */
export default function BookingForm({
    lang,
    selectedDate, maxDate, disableDay, handleDateChange,
    boats, selectedBoat, boatsStatus,
    coaches, selectedCoach, coachesStatus,
    riders, selectedRiders,
    beginners, selectedBeginners,
    timeFrames, selectedStart, setSelectedStart,
    sessionStatus,
    riderName, riderPhone, riderFound,
    handleChange, findSessions, handleGoToBooking,
}) {
    const [slotPickerOpen, setSlotPickerOpen] = useState(false);

    return (
        <>
            {/* ── Calendar column ─────────────────────────────────────── */}
            <Subcontainer item xs={10} md={4} lg={2}>
                <Calendar
                    variant="static"
                    disablePast
                    format="MMMM/dd/yyyy"
                    margin="normal"
                    id="date-picker-inline"
                    label="Date picker inline"
                    shouldDisableDate={disableDay}
                    value={selectedDate}
                    maxDate={maxDate}
                    onChange={handleDateChange}
                    views={['day']}
                    DatePickerToolbar={() => null}
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                />
            </Subcontainer>

            {/* ── Form column ──────────────────────────────────────────── */}
            <Subcontainer item xs={10} md={3}>
                <Grid container direction="column" justifyContent="center">

                    {/* Dropdowns */}
                    <SelectForm arrayName="boats"     items={boats}     value={selectedBoat}      label={t(lang, 'boat')}        onChange={handleChange} lang={lang} status={boatsStatus} />
                    <SelectForm arrayName="coaches"   items={coaches}   value={selectedCoach}     label={t(lang, 'coach')}       onChange={handleChange} lang={lang} status={coachesStatus} />
                    <SelectForm arrayName="riders"    items={riders}    value={selectedRiders}    label={t(lang, 'totalRiders')} onChange={handleChange} lang={lang} />
                    <SelectForm arrayName="beginners" items={beginners} value={selectedBeginners} label={t(lang, 'rookies')}     onChange={handleChange} lang={lang} />

                    {/* Find Sessions */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={findSessions}
                        style={{ marginBottom: '24px' }}
                        disabled={selectedBeginners > 0 && (selectedCoach === '' || selectedCoach === NO_COACH_ID)}
                    >
                        {t(lang, 'findSessions')}
                    </Button>

                    {/* Session status messages */}
                    {sessionStatus === 'Found'        && <Typography variant="subtitle2" style={{ color: 'green', marginBottom: 7 }}>{t(lang, 'sessionsFound')}</Typography>}
                    {sessionStatus === 'Not Found'    && <Typography variant="subtitle2" style={{ color: 'red',   marginBottom: 7 }}>{t(lang, 'noSessionsFound')}</Typography>}
                    {sessionStatus === 'Error'        && <Typography variant="subtitle2" style={{ color: 'red',   marginBottom: 7 }}>{t(lang, 'sessionSearchError')}</Typography>}
                    {sessionStatus === 'Looking'      && <Typography variant="subtitle2" style={{ color: 'gray',  marginBottom: 7 }}>{t(lang, 'lookingForSession')}</Typography>}

                    {/* Slot multiselect */}
                    <FormControl fullWidth style={{ marginBottom: '15px' }} disabled={sessionStatus !== 'Found'}>
                        <InputLabel id="session-multiselect-label" shrink={true}>
                            {t(lang, 'availableSlots')}
                        </InputLabel>
                        <Select
                            labelId="session-multiselect-label"
                            multiple
                            open={slotPickerOpen}
                            onOpen={() => setSlotPickerOpen(true)}
                            onClose={() => setSlotPickerOpen(false)}
                            value={selectedStart instanceof Array ? selectedStart : (selectedStart ? [selectedStart] : [])}
                            name="startMulti"
                            onChange={e => {
                                const value = e.target.value;
                                if (value.length <= 1) {
                                    setSelectedStart(value);
                                } else {
                                    const selectedFrames = value
                                        .map(v => timeFrames.find(f => f[0] === v))
                                        .filter(Boolean)
                                        .sort((a, b) => a[0].localeCompare(b[0]));
                                    if (isTrulyConsecutive(selectedFrames)) {
                                        setSelectedStart(selectedFrames.map(f => f[0]));
                                    }
                                }
                            }}
                            renderValue={selected => {
                                if (!selected || selected.length === 0) return '';
                                if (selected.length === 1) {
                                    const frame = timeFrames.find(f => f[0] === selected[0]);
                                    return frame ? `${frame[0]} - ${frame[1]}` : selected[0];
                                }
                                const first = timeFrames.find(f => f[0] === selected[0]);
                                const last  = timeFrames.find(f => f[0] === selected[selected.length - 1]);
                                return (first && last) ? `${first[0]} - ${last[1]}` : selected.join(', ');
                            }}
                            label={t(lang, 'availableSlots')}
                            displayEmpty
                        >
                            <MenuItem disabled style={{ whiteSpace: 'normal', fontStyle: 'italic', opacity: 0.7 }}>
                                <>{t(lang, 'slotsHintLine1')}<br />{t(lang, 'slotsHintLine2')}</>
                            </MenuItem>

                            {timeFrames.length > 0 ? timeFrames.map((frame, idx) => {
                                const selected = selectedStart instanceof Array ? selectedStart : (selectedStart ? [selectedStart] : []);
                                let disabled = false;
                                if (selected.length > 0) {
                                    const selFrames = selected
                                        .map(s => timeFrames.find(f => f[0] === s))
                                        .filter(Boolean)
                                        .sort((a, b) => a[0].localeCompare(b[0]));
                                    const first = selFrames[0];
                                    const last  = selFrames[selFrames.length - 1];
                                    if (!selected.includes(frame[0]) && frame[1] !== first[0] && frame[0] !== last[1]) {
                                        disabled = true;
                                    }
                                }
                                return (
                                    <MenuItem value={frame[0]} key={idx} disabled={disabled}>
                                        <Checkbox
                                            checked={(selectedStart instanceof Array ? selectedStart : (selectedStart ? [selectedStart] : [])).includes(frame[0])}
                                            style={{ marginRight: 8 }}
                                        />
                                        {frame[0]} - {frame[1]}
                                    </MenuItem>
                                );
                            }) : (
                                <MenuItem value="" disabled>{t(lang, 'noAvailableSlots')}</MenuItem>
                            )}

                            <Box
                                position="sticky"
                                bottom={0}
                                bgcolor="background.paper"
                                borderTop="1px solid"
                                borderColor="divider"
                                px={1}
                                py={0.75}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="small"
                                    onClick={e => { e.stopPropagation(); setSlotPickerOpen(false); }}
                                >
                                    {t(lang, 'confirmSlots')}
                                </Button>
                            </Box>
                        </Select>
                    </FormControl>

                    {/* Contact details */}
                    <Contact variant="outlined">
                        <Typography justify="center">{t(lang, 'contactDetails')}</Typography>
                        <InputContainer noValidate>
                            <Input required value={riderName}  name="name"  onChange={handleChange} label={t(lang, 'name')}  variant="outlined" />
                            <Input required value={riderPhone} name="phone" onChange={handleChange} label={t(lang, 'phone')} variant="outlined" type="tel" />
                            <Subtitle variant="caption">{t(lang, 'phoneFormat')}</Subtitle>
                            {riderFound && <Found variant="subtitle1">{t(lang, 'accountFound')}</Found>}
                        </InputContainer>
                    </Contact>

                    {/* Go to booking */}
                    <Button
                        variant="contained"
                        color="success"
                        style={{ marginBottom: '15px' }}
                        disabled={
                            !(timeFrames.length > 0 && selectedStart && selectedStart.length > 0 &&
                              riderName.length > 1 && riderPhone.replace('+', '').length > 11)
                            || (selectedBeginners > 0 && (selectedCoach === '' || selectedCoach === NO_COACH_ID))
                        }
                        onClick={handleGoToBooking}
                    >
                        {t(lang, 'goToBooking')}
                    </Button>
                </Grid>
            </Subcontainer>
        </>
    );
}
